import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { ApiEndpoints } from 'src/constants/endpoints/apiEndpoints';

/**
 * Project dashboard realtime — singleton `HubConnection` to `ApiEndpoints.hubs.projectDashboard`
 * (ASP.NET Core hub type `ProjectDashboardHub`, URL `/hubs/projectDashboard`).
 *
 * **Server → client** (subscribe via `subscribeProjectDashboard`): `OnInboxEvent`, `OnContainerEvent`,
 * `OnFolderEvent`, `OnProjectHotelsEvent`, `OnProjectBookingsEvent`, `OnProjectContainerEvent`,
 * `OnProjectAssessmentEvent` (PascalCase; camelCase aliases are also registered for JSON protocol quirks).
 *
 * **Client → server** (invoke): `JoinInbox`, `JoinContainer(containerId)`, `JoinProjectContainers(companyId)`.
 * Matches groups: global inbox (`project-intake-inbox`), `project-container-{id}`, `project-containers-company-{companyId}`.
 *
 * **Reconnect**: SignalR does not replay missed messages. Callbacks registered with `onProjectDashboardReconnect`
 * should refetch authoritative state; join helpers re-invoke RPCs after `onreconnected` so groups stay correct.
 *
 * **State**: `ProjectDashboardRealtimeBridge` fans hub events into `useProjectDashboardRealtimeStore` (Zustand);
 * list queries merge REST data with that store (see `useProjectIntakeInboxQuery`, `useMyAssignedProjectContainersQuery`,
 * `useProjectSectionQuery`). Manage Projects table pages still use React Query patches from the bridge.
 */

type ProjectDashboardEvents = {
  OnInboxEvent: (...args: unknown[]) => void;
  OnContainerEvent: (...args: unknown[]) => void;
  OnFolderEvent: (...args: unknown[]) => void;
  OnProjectHotelsEvent: (...args: unknown[]) => void;
  OnProjectBookingsEvent: (...args: unknown[]) => void;
  OnProjectContainerEvent: (...args: unknown[]) => void;
  OnProjectAssessmentEvent: (...args: unknown[]) => void;
};

type EventName = keyof ProjectDashboardEvents;

const eventNames: EventName[] = [
  'OnInboxEvent',
  'OnContainerEvent',
  'OnFolderEvent',
  'OnProjectHotelsEvent',
  'OnProjectBookingsEvent',
  'OnProjectContainerEvent',
  'OnProjectAssessmentEvent'
];

/** Hub may emit camelCase targets to JS depending on server JSON options; subscribe to both. */
const clientEventAliases = (name: EventName): string[] => {
  const camel = name.charAt(0).toLowerCase() + name.slice(1);
  return camel === name ? [name] : [name, camel];
};

const listeners: { [K in EventName]: Set<ProjectDashboardEvents[K]> } = {
  OnInboxEvent: new Set(),
  OnContainerEvent: new Set(),
  OnFolderEvent: new Set(),
  OnProjectHotelsEvent: new Set(),
  OnProjectBookingsEvent: new Set(),
  OnProjectContainerEvent: new Set(),
  OnProjectAssessmentEvent: new Set()
};

let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;
const reconnectCallbacks = new Set<() => void>();

/**
 * Internal helper that ensures the singleton connection exists, has all
 * aggregating `.on(...)` handlers attached, and is started. Callers may
 * safely `void` this – they do not need to `await` it from React code.
 */
const attachProjectDashboardHandlers = (conn: HubConnection): void => {
  for (const eventName of eventNames) {
    const forward = (...args: unknown[]) => {
      const handlers = listeners[eventName];
      for (const handler of handlers) {
        try {
          handler(...args);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    };

    for (const alias of clientEventAliases(eventName)) {
      conn.on(alias, forward);
    }
  }

  conn.onclose((error) => {
    console.warn('[ProjectDashboardSignalR]', new Date().toISOString(), 'WebSocket closed', error ?? '');
  });

  conn.onreconnecting((error) => {
    console.warn('[ProjectDashboardSignalR]', new Date().toISOString(), 'reconnecting…', error ?? '');
  });

  conn.onreconnected(() => {
    for (const cb of reconnectCallbacks) {
      cb();
    }
  });
};

export const ensureConnection = async (): Promise<HubConnection> => {
  if (!connection) {
    connection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.hubs.projectDashboard, {
        // Match axios `withCredentials` so auth cookies reach negotiate/WebSocket on cross-origin dev setups.
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

    // Register before .start() so no server message is handled before listeners exist (SignalR guidance).
    attachProjectDashboardHandlers(connection);
  }

  if (connection.state === HubConnectionState.Connected) {
    return connection;
  }

  if (!startPromise) {
    startPromise = connection
      .start()
      .catch((error) => {
        Sentry.captureException(error);
        connection = null;
        throw error;
      })
      .finally(() => {
        startPromise = null;
      });
  }

  await startPromise;
  return connection!;
};

// Fire-and-forget wrapper used from React code so hooks can stay
// synchronous. This kicks off connection creation/start in the
// background but does not block the caller.
const ensureConnectionStarted = (): void => {
  void ensureConnection();
};

// Subscribe to one or more hub events. This is intentionally synchronous so
// React hooks can use it in a standard `useEffect` pattern. The underlying
// connection is started in the background via `ensureConnectionStarted`.
export const subscribeProjectDashboard = (handlers: Partial<ProjectDashboardEvents>): (() => void) => {
  // Kick off connection creation/start but don't wait for it here.
  ensureConnectionStarted();

  (Object.entries(handlers) as [EventName, ProjectDashboardEvents[EventName]][]).forEach(([eventName, handler]) => {
    if (!handler) return;
    listeners[eventName].add(handler as any);
  });

  return () => {
    (Object.entries(handlers) as [EventName, ProjectDashboardEvents[EventName]][]).forEach(([eventName, handler]) => {
      if (!handler) return;
      listeners[eventName].delete(handler as any);
    });
  };
};

// Allow features to register actions that should run after any reconnect
// (e.g., re-joining groups or refreshing critical queries).
export const onProjectDashboardReconnect = (cb: () => void): (() => void) => {
  reconnectCallbacks.add(cb);
  return () => {
    reconnectCallbacks.delete(cb);
  };
};

// Helper to invoke server-side hub methods in a type-agnostic way.
export const invokeProjectDashboard = async (methodName: string, ...args: unknown[]): Promise<unknown> => {
  const conn = await ensureConnection();
  return conn.invoke(methodName, ...args);
};

const invokeJoin = (methodName: string, ...args: unknown[]): void => {
  void ensureConnection()
    .then((conn) =>
      conn.invoke(methodName, ...args).catch((error) => {
        Sentry.captureException(error);
        console.warn('[ProjectDashboardSignalR]', new Date().toISOString(), `${methodName} failed`, ...args, error);
      })
    )
    .catch((error) => {
      console.warn('[ProjectDashboardSignalR]', new Date().toISOString(), `${methodName} failed`, ...args, error);
    });
};

const joinWithReconnect = (action: () => void): (() => void) => {
  action();
  reconnectCallbacks.add(action);
  return () => {
    reconnectCallbacks.delete(action);
  };
};

export const joinProjectContainer = (containerId: number): (() => void) =>
  joinWithReconnect(() => invokeJoin('JoinContainer', containerId));

export const joinProjectContainersForCompany = (companyId: number): (() => void) =>
  joinWithReconnect(() => invokeJoin('JoinProjectContainers', companyId));

export const joinProjectInbox = (): (() => void) => joinWithReconnect(() => invokeJoin('JoinInbox'));
